'use client'

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { AlertCircle, Clock, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { EmptyState, LoadingState } from './EmptyState'
import { usePageRefresh, type PageRefreshState } from '../shell/pageChrome'
import { StickyStackContext, useStickyStackTop } from '../stickyStack'
import { useBrandMask } from '../shell/brandMask'

// A page (the design system's .main-content): the heading -- a mono eyebrow
// "TRUSTFENCE / SECTION", the h1 ending in a signal-coloured period, an
// optional (i), and on the right the timezone, the page's own controls and
// the primary action -- then an optional metrics strip (hairline top, cells
// divided by hairlines, nothing card-like), then the page's modules, then
// the mono footer. The page's refresh state goes to the topbar.

export type MetricTone = 'default' | 'signal' | 'success' | 'warning' | 'danger'
export type MetricDef = { label: ReactNode; value: ReactNode; tone?: MetricTone; note?: ReactNode; title?: string }

const METRIC_TONE: Record<MetricTone, string> = {
  default: 'text-primary',
  signal: 'text-signal',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-destructive',
}

export function Metric({ label, value, tone = 'default', note, title }: MetricDef) {
  return (
    <div title={title} className="min-w-0 px-7 first:pl-0 last:pr-0">
      <div className="font-mono text-micro uppercase tracking-[0.08em] text-muted-foreground">{label}</div>
      <div className={`mt-2 flex items-baseline gap-3 text-metric font-normal tabular-nums tracking-[-0.015em] ${METRIC_TONE[tone]}`}>
        {value}
        {note && <span className="whitespace-nowrap font-mono text-label text-subtle">{note}</span>}
      </div>
    </div>
  )
}

export function MetricsStrip({ metrics, className = '' }: { metrics: MetricDef[]; className?: string }) {
  return (
    <div className={`mx-auto grid w-full max-w-[1240px] divide-x divide-border border-t border-border py-[22px] ${className}`} style={{ gridTemplateColumns: `repeat(${metrics.length}, minmax(0, 1fr))` }}>
      {metrics.map((m, i) => <Metric key={i} {...m} />)}
    </div>
  )
}

// The page heading, pinned: it sticks under the shell's topbar (whose
// height comes through the sticky stack), so the eyebrow, the title and the
// page's own controls stay in view however far the page scrolls
// (2026-10-22: every view shows its header as a header). It measures
// itself and reports the height so what sticks below it -- a table's
// toolbar and column headers -- stacks under it instead of sliding behind.
// A string eyebrow, title or subtitle goes through the demo brand mask
// (shell/brandMask.tsx): "CTT · Moderation" reads "ID-K3Q9X · Moderation".
export function PageHeading({
  eyebrow, title, info, subtitle, actions, timezone, gutters = 'outset', onHeight, top,
}: {
  eyebrow: ReactNode
  title: ReactNode
  info?: ReactNode
  subtitle?: ReactNode
  /** The right cluster: the page's controls, then its primary action. */
  actions?: ReactNode
  timezone?: string | null
  /** 'outset' when the page root carries the gutters (the bar bleeds into
   * them with negative margins); 'inset' when the view keeps its own
   * padding and the bar must bring the gutters itself. */
  gutters?: 'outset' | 'inset'
  onHeight?: (px: number) => void
  /** Where the bar pins: the chrome above it (the topbar). A frame that
   * publishes `topbar + heading` to its children must pass this, because
   * read from inside that provider the value already holds the heading's
   * own height and the bar pins one heading below the topbar (the gap seen
   * 2026-10-22). Left out, the bar reads the stack it is rendered in. */
  top?: number
}) {
  const ctxTop = useStickyStackTop()
  const stackTop = top ?? ctxTop
  const { mask } = useBrandMask()
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el || !onHeight) return
    const report = () => onHeight(el.offsetHeight)
    const ro = new ResizeObserver(report)
    ro.observe(el)
    report()
    return () => ro.disconnect()
  }, [onHeight])
  const gutterCls = gutters === 'outset' ? '-mx-5 px-5 lg:-mx-10 lg:px-10' : 'px-5 lg:px-10'
  return (
    <div ref={ref} style={{ top: stackTop }} className={`sticky z-20 mb-6 border-b border-border bg-background pb-4 pt-6 ${gutterCls}`}>
      <div className="mx-auto flex w-full max-w-[1240px] flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <span className="mb-2 block font-mono text-label uppercase tracking-[0.1em] text-subtle">{typeof eyebrow === 'string' ? mask(eyebrow) : eyebrow}</span>
          <h1 className="flex items-center gap-2 text-h1 font-normal tracking-[-0.022em] text-foreground">
            <span>{typeof title === 'string' ? mask(title) : title}<span className="text-brand-magenta">.</span></span>
            {info && <InfoPopover content={info} />}
          </h1>
          {subtitle && <p className="mt-1.5 max-w-3xl text-nav text-muted-foreground">{typeof subtitle === 'string' ? mask(subtitle) : subtitle}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-5 pb-1">
          {timezone && (
            <span className="flex items-center gap-1.5 font-mono text-label text-muted-foreground"><Clock className="h-3 w-3" />{timezone}</span>
          )}
          {actions}
        </div>
      </div>
    </div>
  )
}

// What a page shows under its heading while it has no body yet: the
// loading pulse, or the error with a Retry. The heading is the frame's,
// so it is there before the data is (2026-10-22: Unified Entries used to
// return a bare loading line and the header appeared after it).
export function pageBodyState({ loading, error, onRetry }: { loading?: boolean; error?: ReactNode; onRetry?: () => void }): ReactNode | null {
  if (loading) return <LoadingState className="py-24" />
  if (error) {
    return (
      <EmptyState icon={AlertCircle} title="Could not load this page" action={onRetry ? <Button variant="outline" onClick={onRetry}>Retry</Button> : undefined}>
        {error}
      </EmptyState>
    )
  }
  return null
}

export function PageFrame({
  eyebrow, title, info, subtitle, children, metrics, primaryAction, headerActions, timezone = 'Europe/Lisbon',
  footer, refresh, className = '', rootRef, wide = false, padded = true, fill = false, loading, error, onRetry,
}: {
  /** "Operations", "CTT · Moderation": the mono line over the title. */
  eyebrow: ReactNode
  /** The page name; the period is added. */
  title: ReactNode
  info?: ReactNode
  subtitle?: ReactNode
  children?: ReactNode
  metrics?: MetricDef[]
  primaryAction?: ReactNode
  headerActions?: ReactNode
  timezone?: string | null
  footer?: ReactNode
  /** The page's refresh state, shown in the topbar. */
  refresh?: PageRefreshState
  className?: string
  rootRef?: React.Ref<HTMLDivElement>
  /** Lets the modules span the full content width (a wide table) instead of 1240 px. */
  wide?: boolean
  /** false: the frame is the heading alone -- no gutters, no module column,
   * no footer; the view keeps its own body layout under the pinned heading
   * (the admin views, the two-pane workspaces). */
  padded?: boolean
  /** The body takes exactly the space left under the topbar and the heading
   * and clips; the view scrolls its own panels (the logs, Manage pages). */
  fill?: boolean
  /** While true the body is the loading pulse under the heading. */
  loading?: boolean
  /** When set (and not loading) the body is this error with a Retry. */
  error?: ReactNode
  onRetry?: () => void
}) {
  usePageRefresh(refresh ?? {})
  const stackTop = useStickyStackTop()
  const [headingHeight, setHeadingHeight] = useState(0)
  const stacked = stackTop + headingHeight
  const state = pageBodyState({ loading, error, onRetry })
  const body = state != null
    ? state
    : padded
    ? (
      <>
        {metrics && metrics.length > 0 && <MetricsStrip metrics={metrics} className="mb-11" />}
        <div className={`mx-auto flex w-full flex-col gap-10 [counter-reset:module] ${wide ? '' : 'max-w-[1240px]'}`}>{children}</div>
        <div className="mx-auto mt-8 flex w-full max-w-[1240px] flex-wrap items-center justify-between gap-3 font-mono text-micro text-subtle">
          <span>{footer ?? (timezone ? `All times in ${timezone}.` : null)}</span>
        </div>
      </>
    )
    : fill
      ? <div style={{ height: `calc(100vh - ${stacked}px)` }} className="flex min-h-0 flex-col overflow-hidden">{children}</div>
      : children
  return (
    <StickyStackContext.Provider value={stacked}>
      <div ref={rootRef} className={`mx-auto w-full max-w-[1840px] ${padded ? 'px-5 pb-10 lg:px-10' : ''} ${className}`}>
        <PageHeading
          eyebrow={eyebrow} title={title} info={info} subtitle={subtitle} timezone={padded ? timezone : null}
          gutters={padded ? 'outset' : 'inset'} onHeight={setHeadingHeight} top={stackTop}
          actions={<>{headerActions}{primaryAction}</>}
        />
        {body}
      </div>
    </StickyStackContext.Provider>
  )
}

// The (i) next to a title: a popover on the card surface with a hairline.
export function InfoPopover({ content }: { content: ReactNode }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])
  return (
    <span ref={ref} className="relative inline-flex">
      <button type="button" onClick={() => setOpen((v) => !v)} title="About this page" className={`flex h-5 w-5 items-center justify-center rounded-[2px] transition-colors ${open ? 'text-signal' : 'text-subtle hover:text-signal'}`}>
        <Info className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-30 mt-2 w-80 border border-border bg-card p-3 text-small font-normal leading-relaxed tracking-normal text-foreground shadow-notice">
          <div className="mb-1.5 font-mono text-micro uppercase tracking-widest text-subtle">Data source</div>
          {content}
        </div>
      )}
    </span>
  )
}
